import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PageShell } from "@/components/site/page-shell";
import { cartStore } from "@/lib/cart";
import { wishlistStore, useWishlist } from "@/lib/wishlist";
import { toast } from "sonner";
import {
  Package, Star, ShoppingCart, ArrowLeft, ShieldCheck, Download,
  KeyRound, Clock, ChevronRight, AlertTriangle, RefreshCw, Heart,
  ChevronLeft, ArrowUpRight, Lock, Zap,
} from "lucide-react";

type Item = {
  id: string;
  category_id: string | null;
  slug: string;
  name: string;
  short_description: string | null;
  description: string | null;
  price_cents: number;
  price_unit: string | null;
  currency: string;
  image_url: string | null;
  badge: string | null;
  is_top: boolean;
  featured: boolean;
  rating_avg: number;
  rating_count: number;
  stock_qty: number | null;
  stock_tracked: boolean;
  requires_license: boolean;
  file_size_label: string | null;
  metadata: Record<string, any> | null;
};

function formatPrice(cents: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function isComingSoon(it: Item | null | undefined) {
  const m = it?.metadata?.coming_soon;
  return m === true || m === "true";
}
function isOutOfStock(it: Item | null | undefined) {
  return !!it?.stock_tracked && (it?.stock_qty ?? 0) <= 0;
}

const itemQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["public", "digital-item", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("digital_items")
        .select("*")
        .eq("slug", slug)
        .eq("active", true)
        .maybeSingle();
      if (error) throw error;
      return data as Item | null;
    },
    staleTime: 60_000,
  });

export const Route = createFileRoute("/marketplace/item/$slug")({
  component: ItemPage,
  loader: async ({ params, context }) => {
    const item = await context.queryClient.ensureQueryData(itemQueryOptions(params.slug));
    return { item };
  },
  head: ({ params, loaderData }) => {
    const item = (loaderData as { item: Item | null } | undefined)?.item ?? null;
    const meta_: Record<string, any> = (item?.metadata as any) ?? {};
    const seoTitle: string | undefined = meta_.seo_title || meta_.meta_title;
    const seoDesc: string | undefined = meta_.seo_description || meta_.meta_description;
    const seoImage: string | undefined = meta_.og_image || meta_.seo_image || item?.image_url || undefined;
    const seoKeywords: string | undefined = Array.isArray(meta_.tags)
      ? meta_.tags.join(", ")
      : typeof meta_.keywords === "string"
        ? meta_.keywords
        : undefined;

    const title = seoTitle || (item ? `${item.name} — CoderLity Marketplace` : `${params.slug} — Marketplace`);
    const desc =
      seoDesc ||
      item?.short_description ||
      item?.description?.slice(0, 155) ||
      "Digital product on the CoderLity marketplace — ready-to-ship, instantly delivered.";
    const url = `/marketplace/item/${params.slug}`;

    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "product" },
      { property: "og:url", content: url },
      { property: "og:site_name", content: "CoderLity" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ];
    if (seoKeywords) meta.push({ name: "keywords", content: seoKeywords });
    if (seoImage) {
      meta.push({ property: "og:image", content: seoImage });
      meta.push({ property: "og:image:alt", content: item?.name || params.slug });
      meta.push({ name: "twitter:image", content: seoImage });
    }
    if (item) {
      meta.push({ property: "product:price:amount", content: (item.price_cents / 100).toFixed(2) });
      meta.push({ property: "product:price:currency", content: item.currency || "USD" });
    }

    const scripts = item
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              name: item.name,
              description: desc,
              image: seoImage ? [seoImage] : undefined,
              sku: item.slug,
              brand: { "@type": "Brand", name: "CoderLity" },
              offers: {
                "@type": "Offer",
                priceCurrency: item.currency || "USD",
                price: (item.price_cents / 100).toFixed(2),
                availability: isComingSoon(item)
                  ? "https://schema.org/PreOrder"
                  : isOutOfStock(item)
                    ? "https://schema.org/OutOfStock"
                    : "https://schema.org/InStock",
                url,
              },
              aggregateRating:
                item.rating_count > 0
                  ? {
                      "@type": "AggregateRating",
                      ratingValue: item.rating_avg,
                      reviewCount: item.rating_count,
                    }
                  : undefined,
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Marketplace", item: "/marketplace" },
                { "@type": "ListItem", position: 2, name: item.name, item: url },
              ],
            }),
          },
        ]
      : undefined;

    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  errorComponent: ({ error, reset }) => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-destructive" />
        <h1 className="mt-3 text-xl font-bold">Failed to load item</h1>
        <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
        <button onClick={reset} className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
          <RefreshCw className="h-4 w-4" /> Retry
        </button>
      </div>
    </PageShell>
  ),
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-black">Item not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">This product may have been removed or renamed.</p>
        <Link to="/marketplace" className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to marketplace
        </Link>
      </div>
    </PageShell>
  ),
});

function ItemPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const wishlist = useWishlist();

  const { data: item, isError, refetch, isFetching } = useSuspenseQuery(itemQueryOptions(slug));

  const comingSoon = isComingSoon(item);
  const outOfStock = isOutOfStock(item);
  const disabled = comingSoon || outOfStock;
  const inWishlist = item ? wishlist.some((w) => w.id === item.id) : false;

  // Related items by category
  const relatedQ = useQuery({
    queryKey: ["public", "digital-item", "related", item?.category_id, item?.id],
    enabled: !!item?.category_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("digital_items")
        .select("id,slug,name,short_description,price_cents,currency,image_url,badge,metadata,rating_avg,rating_count,is_top")
        .eq("active", true)
        .eq("category_id", item!.category_id!)
        .neq("id", item!.id)
        .order("is_top", { ascending: false })
        .order("featured", { ascending: false })
        .limit(8);
      if (error) throw error;
      return (data ?? []) as Item[];
    },
    staleTime: 60_000,
  });

  const addToCart = (opts?: { thenGoTo?: "cart" | "checkout" }) => {
    if (!item || disabled) return;
    cartStore.add({
      id: item.id,
      name: item.name,
      price: item.price_cents / 100,
      meta: item.price_unit || undefined,
      href: `/marketplace/item/${item.slug}`,
    });
    toast.success(`${item.name} added to cart`);
    if (opts?.thenGoTo === "cart") navigate({ to: "/cart" });
    if (opts?.thenGoTo === "checkout") navigate({ to: "/checkout" });
  };

  const toggleWishlist = () => {
    if (!item) return;
    wishlistStore.toggle({
      id: item.id,
      slug: item.slug,
      name: item.name,
      price_cents: item.price_cents,
      currency: item.currency,
      image_url: item.image_url,
    });
    toast.success(inWishlist ? "Removed from favorites" : "Saved to favorites");
  };

  const notifyMe = () => {
    if (!item) return;
    toggleWishlist();
    toast.success("We'll notify you when it launches");
  };

  // Related scroller
  const relRef = useRef<HTMLDivElement>(null);
  const scrollRel = (dir: "left" | "right") => {
    const el = relRef.current;
    if (!el) return;
    const step = 300;
    el.scrollBy({ left: dir === "left" ? -step : step, behavior: "smooth" });
  };

  if (!item) {
    return (
      <PageShell>
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <h1 className="text-2xl font-black">Item not found</h1>
          <Link to="/marketplace" className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to marketplace
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Breadcrumb */}
      <section className="border-b border-border/60 bg-muted/20">
        <div className="mx-auto max-w-6xl px-4 py-4 lg:px-8">
          <nav className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/marketplace" className="hover:text-foreground">Marketplace</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground line-clamp-1">{item.name}</span>
          </nav>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 lg:px-8">
        {isError && (
          <div className="mb-6 rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-destructive" />
            <p className="mt-3 text-sm">Couldn't refresh this item.</p>
            <button onClick={() => refetch()} disabled={isFetching}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Retry
            </button>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,4fr)] lg:gap-12">
          {/* Image */}
          <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-fuchsia-500/5">
            {item.image_url ? (
              <img src={item.image_url} alt={item.name} className="h-full w-full object-cover aspect-square" />
            ) : (
              <div className="grid aspect-square place-items-center">
                <Package className="h-24 w-24 text-primary/40" />
              </div>
            )}
            {comingSoon && (
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-amber-500/90 py-2 text-xs font-bold uppercase tracking-wide text-black">
                <Clock className="h-4 w-4" /> Coming Soon
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              {item.badge && (
                <span className="rounded-full bg-primary/15 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-primary">
                  {item.badge}
                </span>
              )}
              {item.is_top && (
                <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                  Top pick
                </span>
              )}
              {comingSoon && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                  <Clock className="h-3 w-3" /> Coming soon
                </span>
              )}
              {outOfStock && !comingSoon && (
                <span className="rounded-full bg-destructive/15 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-destructive">
                  Out of stock
                </span>
              )}
            </div>

            <h1 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">{item.name}</h1>

            {item.rating_count > 0 && (
              <div className="mt-3 flex items-center gap-2 text-sm">
                <span className="inline-flex items-center gap-1">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  <strong>{item.rating_avg.toFixed(1)}</strong>
                </span>
                <span className="text-muted-foreground">({item.rating_count} reviews)</span>
              </div>
            )}

            {item.short_description && (
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                {item.short_description}
              </p>
            )}

            <div className="mt-6 flex items-end gap-3">
              <span className="text-4xl font-black text-primary">
                {formatPrice(item.price_cents, item.currency)}
              </span>
              {item.price_unit && (
                <span className="pb-1 text-sm text-muted-foreground">/ {item.price_unit}</span>
              )}
            </div>

            {/* CTAs */}
            <div className="mt-6 flex flex-wrap gap-3">
              {comingSoon ? (
                <button type="button" onClick={notifyMe}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-semibold text-black shadow-lg shadow-amber-500/20 transition hover:brightness-110">
                  <Clock className="h-4 w-4" /> Notify me
                </button>
              ) : (
                <>
                  <button type="button" onClick={() => addToCart({ thenGoTo: "checkout" })}
                    disabled={outOfStock}
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60">
                    <Zap className="h-4 w-4" /> Buy now
                  </button>
                  <button type="button" onClick={() => addToCart()}
                    disabled={outOfStock}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold hover:bg-muted transition disabled:cursor-not-allowed disabled:opacity-60">
                    <ShoppingCart className="h-4 w-4" />
                    {outOfStock ? "Out of stock" : "Add to cart"}
                  </button>
                </>
              )}
              <button type="button" onClick={toggleWishlist} aria-pressed={inWishlist}
                aria-label={inWishlist ? "Remove from favorites" : "Save to favorites"}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-3 text-sm font-semibold transition ${
                  inWishlist
                    ? "border-rose-500/40 bg-rose-500/10 text-rose-600"
                    : "border-border bg-background hover:bg-muted"
                }`}>
                <Heart className={`h-4 w-4 ${inWishlist ? "fill-rose-500 text-rose-500" : ""}`} />
                {inWishlist ? "Saved" : "Save"}
              </button>
            </div>

            <div className="mt-3">
              <Link to="/cart" className="text-xs font-semibold text-primary hover:underline">
                View cart →
              </Link>
            </div>

            {/* Trust row */}
            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-card p-3">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <p className="mt-1.5 text-xs font-semibold">Secure checkout</p>
                <p className="text-[11px] text-muted-foreground">Encrypted payments</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-3">
                <Download className="h-4 w-4 text-primary" />
                <p className="mt-1.5 text-xs font-semibold">Instant delivery</p>
                <p className="text-[11px] text-muted-foreground">{item.file_size_label ?? "Digital download"}</p>
              </div>
              {item.requires_license && (
                <div className="rounded-xl border border-border bg-card p-3">
                  <KeyRound className="h-4 w-4 text-primary" />
                  <p className="mt-1.5 text-xs font-semibold">License included</p>
                  <p className="text-[11px] text-muted-foreground">Auto-issued key</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Long description */}
        {item.description && (
          <div className="mt-12 grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="text-xl font-bold">About this item</h2>
              <div className="mt-3 text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
                {item.description}
              </div>
            </div>
            <aside className="rounded-2xl border border-border bg-card p-5 h-fit">
              <h3 className="text-sm font-bold">Product details</h3>
              <dl className="mt-3 space-y-2 text-xs">
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Price</dt>
                  <dd className="font-semibold">{formatPrice(item.price_cents, item.currency)}</dd>
                </div>
                {item.price_unit && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Unit</dt>
                    <dd className="font-semibold">{item.price_unit}</dd>
                  </div>
                )}
                {item.file_size_label && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Size</dt>
                    <dd className="font-semibold">{item.file_size_label}</dd>
                  </div>
                )}
                {item.stock_tracked && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Availability</dt>
                    <dd className="font-semibold">
                      {(item.stock_qty ?? 0) > 0 ? `${item.stock_qty} in stock` : "Out of stock"}
                    </dd>
                  </div>
                )}
              </dl>
            </aside>
          </div>
        )}

        {/* Related items */}
        {relatedQ.data && relatedQ.data.length > 0 && (
          <div className="mt-16">
            <div className="mb-5 flex items-end justify-between gap-3">
              <div>
                <h2 className="text-xl font-black tracking-tight sm:text-2xl">You might also like</h2>
                <p className="mt-1 text-xs text-muted-foreground">More from the same category.</p>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <button type="button" onClick={() => scrollRel("left")} aria-label="Scroll left"
                  className="grid h-9 w-9 place-items-center rounded-full border border-border bg-background hover:bg-muted transition">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => scrollRel("right")} aria-label="Scroll right"
                  className="grid h-9 w-9 place-items-center rounded-full border border-border bg-background hover:bg-muted transition">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div ref={relRef}
              className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 -mx-4 px-4 lg:mx-0 lg:px-0 [scrollbar-width:thin]">
              {relatedQ.data.map((rel) => {
                const soon = isComingSoon(rel);
                return (
                  <Link
                    key={rel.id}
                    to="/marketplace/item/$slug"
                    params={{ slug: rel.slug }}
                    className={`group relative shrink-0 snap-start overflow-hidden rounded-2xl border p-4 w-[70%] sm:w-[45%] md:w-[31%] lg:w-[23%] transition ${
                      soon ? "border-dashed border-border bg-card/60" : "border-border bg-card hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg"
                    }`}
                  >
                    <div className="aspect-video overflow-hidden rounded-xl bg-muted">
                      {rel.image_url ? (
                        <img src={rel.image_url} alt="" className={`h-full w-full object-cover ${soon ? "opacity-60" : ""}`} />
                      ) : (
                        <div className="grid h-full place-items-center">
                          <Package className="h-8 w-8 text-primary/40" />
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex items-start justify-between gap-2">
                      <h3 className={`text-sm font-bold leading-tight line-clamp-2 ${soon ? "text-muted-foreground" : ""}`}>
                        {rel.name}
                      </h3>
                      {soon && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-600 dark:text-amber-400">
                          <Clock className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className={`text-sm font-black ${soon ? "text-muted-foreground" : "text-primary"}`}>
                        {formatPrice(rel.price_cents, rel.currency)}
                      </span>
                      {soon ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                          <Lock className="h-3 w-3" /> Locked
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground group-hover:text-primary transition">
                          View <ArrowUpRight className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-12">
          <Link to="/marketplace" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to marketplace
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
